import React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { MEMBERSHIP_TYPE_CONFIGURATION_RESOURCE } from 'src/features/membershipType/api/useGetMembershipTypeConfiguration';
import { LIST_MEMBERSHIP_TYPE_ZONE_RULES_RESOURCE } from 'src/features/membershipType/api/zoneRule/useListMembershipTypeZoneRules';
import { useUpdateMembershipTypeZoneRule } from 'src/features/membershipType/api/zoneRule/useUpdateMembershipTypeZoneRule';
import ZoneRuleFormModal from 'src/features/membershipType/components/forms/zoneRule/ZoneRuleFormModal';
import { ZoneRule } from 'src/features/membershipType/types';
import { zoneRulesMessages } from 'src/features/membershipType/utils/messages';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTableNewEntryProps } from 'src/registry/components/table';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { MEMBERSHIP_TYPE_RESOURCE } from 'src/features/membershipType/api/useGetMembershipType.ts';

interface UpdateZoneRuleFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedZoneRule: ZoneRule;
    children?: React.ReactNode;
}

function UpdateZoneRuleFormModal<TData>({
    className,
    children,
    selectedZoneRule,
}: UpdateZoneRuleFormModalProps<TData>) {
    const { formatMessage, locale } = useIntl();
    const { membershipTypeId = '' } = useParams();
    const { parkingId } = useParking();

    const selectedZoneRuleTranslation =
        selectedZoneRule.zone.translations.find((translation) => translation.language === locale)?.name || '';

    const { mutate, isPending } = useUpdateMembershipTypeZoneRule({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_MEMBERSHIP_TYPE_ZONE_RULES_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_TYPE_CONFIGURATION_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_TYPE_RESOURCE] });

            toast({
                title: formatMessage(zoneRulesMessages.updateZoneRuleSuccess),
                description: formatMessage(zoneRulesMessages.updateZoneRuleSuccessDescription, {
                    entry: success.zone?.translations?.find((translation) => translation.language === locale)?.name,
                }),
            });
        },
    });

    return (
        <ZoneRuleFormModal
            selectedZoneRule={selectedZoneRule}
            className={className}
            mutate={(data, onSuccess, onError) =>
                mutate({ parkingId, membershipTypeId, ruleId: selectedZoneRule.id, ...data }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, {
                entry: formatMessage(zoneRulesMessages.rule).toLowerCase(),
            })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(zoneRulesMessages.editZoneRuleSubtitle, {
                entry: selectedZoneRuleTranslation,
            })}
        >
            {children}
        </ZoneRuleFormModal>
    );
}

export default UpdateZoneRuleFormModal;
